import { ActiveReports, ArchivedReports, Reports, Report } from '@/models/Report';
import { ReportPublications } from '@/models/Publication';
import { make } from 'vuex-pathify';

const state = {
	active: new ActiveReports(),
	archived: new ArchivedReports(),
	currentReport: null,
};

const getters = {

	/**
	 * Returns all reports, active and archived
	 *
	 * @param {Object} state This store
	 * @returns {Projects} Combined active and archived report collection
	 */
	all: (state) => {
		const models = state.active.models.concat(state.archived.models);
		return new Reports(models);
	},

	/**
	 * Finds if store has current report set
	 *
	 * @returns {boolean}
	 */
	hasCurrentReport: (state) => {
		return (state.currentReport !== null && state.currentReport instanceof Report);
	},

};

const mutations = {
	...make.mutations(state),
};

const actions = {

	/**
	 * Load all reports for provided project ID
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {number} projectId Project ID
	 * @returns {Promise} Result of fetching reports
	 */
	loadAll({ state }, projectId) {
		return new Promise((resolve, reject) => {

			// Clear the state
			state.active.clear();
			state.archived.clear();

			const reports = new Reports();
			reports.set('id', projectId);
			reports.fetch().then((resp) => {

				reports.each(report => {
					report.isArchived ? state.archived.add(report) : state.active.add(report);
				});

				resolve(reports);
			}).catch((e) => {
				dump('Error fetching reports for project', e);
				reject(e);
			});
		});
	},

	/**
	 * Loads report by report ID and related data: report publications.
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {number} reportId Report ID
	 * @returns {Promise} Result of fetching report and report publications
	 */
	loadCurrentReportAndRelatedData({ dispatch }, reportId) {

		let promises = [
			dispatch('loadCurrentReport', reportId),
			dispatch('loadReportPublications', reportId),
			dispatch('articlesNew/loadAll', reportId, { root: true }),
		];

		return Promise.all(promises);
	},

	/**
	 * Load report by ID, first, looks in store for this report,
	 * if not there, fetches from backend
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {number} reportId Report ID
	 * @returns {Promise} Result of fething report
	 */
	loadCurrentReport({ getters, commit }, reportId) {
		return new Promise((resolve, reject) => {
			let report = null;
			// Check if report in store already
			try {
				report = getters.all.find({ id: reportId });

				if (report) {
					commit('SET_CURRENT_REPORT', report);
					resolve(report);
					return;
				}
			}
			catch (e) {
				dump('Error finding current report in all reports', e);
				reject(e);
				return;
			}

			report = new Report({ id: reportId });
			commit('SET_CURRENT_REPORT', report);

			report.fetch().then((resp) => {
				resolve(report);
			}).catch((e) => {
				dump('Error fetching current report', e);
				reject(e);
			});
		});
	},

	/**
	 * Load report publications for provided report ID
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {number} reportId Report ID
	 * @returns {Promise} Result of fetching report publications
	 */
	loadReportPublications({ commit }, reportId) {
		return new Promise((resolve, reject) => {
			const publications = new ReportPublications();
			publications.set('id', reportId);
			publications.fetch().then(() => {
				/**
				 * Set to old store as well for now
				 * @todo To be deleted
				 */
				commit('SET_REPORT_PUBLICATIONS', publications.models, { root: true });
				resolve(publications);
			}).catch((e) => {
				dump('Error loading publications for current report', e);
				reject(e);
			});
		});
	},

	/**
	 * Archives report, moves report from active collection to archived
	 * @todo Add check if successfull, maybe return promise with try/catch
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {Report} report Selected report
	 */
	archive({ state }, report) {
		state.active.remove(report);
		state.archived.add(report);
	},

	/**
	 * Restores report, moves report from archived collection to active
	 * @todo Add check if successfull, maybe return promise with try/catch
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {Report} report Selected report
	 */
	restore({ state }, report) {
		state.archived.remove(report);
		state.active.add(report);
	},

};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
